"use client";

import { useReportWebVitals } from "next/web-vitals";

import { rumEnqueue, type RumMetricType } from "./rum-sender";

const TRACKED: Record<string, RumMetricType> = {
  LCP: "LCP",
  INP: "INP",
  CLS: "CLS",
  TTFB: "TTFB",
  FCP: "FCP",
};

/**
 * Уже отправленные пары «метрика + значение» в пределах загрузки страницы (KAN-1396).
 *
 * useReportWebVitals держит колбэк в зависимостях эффекта и на каждый новый колбэк
 * регистрирует onCLS/onINP/… ЗАНОВО — отписки у web-vitals нет. Каждая регистрация
 * отчитывается отдельно и со своим metric.id, поэтому по id дубли не отличить. Копии
 * несут одно и то же значение (их считают по одним и тем же записям производительности),
 * а настоящее обновление INP/CLS значение меняет — оно пройдёт.
 */
const sent = new Set<string>();

/**
 * Модульная функция, а не стрелка в теле компонента: у стрелки новая ссылка на каждый
 * рендер, и корневой layout перерегистрировал web-vitals при каждом своём рендере.
 * На проде это дало, например, 14 одинаковых записей INP за день от одной сессии —
 * и «p75» по маршруту превращался в значение одного пользователя.
 */
function reportVital(metric: { name: string; value: number }): void {
  const type = TRACKED[metric.name];
  if (!type) return;
  // CLS безразмерный (~0..1) — шлём *1000, чтобы хранить в одном double-поле
  // с миллисекундными метриками (договорённость с бэкендом KAN-318).
  const value = metric.name === "CLS" ? metric.value * 1000 : metric.value;
  const key = `${type}:${Math.round(value * 100) / 100}`;
  if (sent.has(key)) return;
  sent.add(key);
  rumEnqueue(type, value);
}

/** Сброс памяти об отправленном — только для тестов. */
export function resetRumVitalsForTests(): void {
  sent.clear();
}

/** KAN-315: сбор Core Web Vitals по маршрутам → POST /api/rum-metrics. */
export function RumVitalsReporter() {
  useReportWebVitals(reportVital);
  return null;
}
